import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { endpoints } from '../../api/api';
import AdminHeader from './components/AdminHeader';

interface StaffUser {
  _id: string;
  name: string;
  email: string;
  role: 'admin' | 'sales' | 'superadmin';
  isActive: boolean;
  createdAt: string;
  lastLogin?: string;
}

const ROLE_LABELS: Record<string, string> = {
  admin: 'Admin',
  sales: 'Sales',
  superadmin: 'Superadmin',
};

const ROLE_BADGE_STYLES: Record<string, string> = {
  admin: 'bg-blue-50 text-blue-700',
  sales: 'bg-amber-50 text-amber-700',
  superadmin: 'bg-gray-800 text-white',
};

const initialForm = { name: '', email: '', password: '', role: 'sales' as 'admin' | 'sales' };

const StaffManagement: React.FC = () => {
  const navigate = useNavigate();
  const [staff, setStaff] = useState<StaffUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(initialForm);
  const [creating, setCreating] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [formSuccess, setFormSuccess] = useState<string | null>(null);

  const fetchStaff = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(endpoints.getStaffUsers);
      setStaff(res.data.staff || []);
    } catch (error) {
      console.error('Failed to fetch staff:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStaff(); }, [fetchStaff]);

  const handleLogout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    navigate('/admin/login');
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    setFormError(null);
    setFormSuccess(null);
    try {
      await axios.post(endpoints.createStaffUser, form);
      setFormSuccess(`${ROLE_LABELS[form.role]} account created for ${form.email}.`);
      setForm(initialForm);
      fetchStaff();
    } catch (err: any) {
      setFormError(err?.response?.data?.message || 'Failed to create account. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  const toggleStatus = async (member: StaffUser) => {
    try {
      await axios.patch(endpoints.updateStaffStatus.replace(':id', member._id), { isActive: !member.isActive });
      setStaff(prev => prev.map(s => s._id === member._id ? { ...s, isActive: !s.isActive } : s));
    } catch (error) {
      console.error('Failed to update staff status:', error);
      alert('Failed to update account status. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <AdminHeader onLogout={handleLogout} title="Staff Management" />
      <main className="px-4 py-8 max-w-5xl mx-auto space-y-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Create staff account</h2>
          <form onSubmit={handleCreate} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
              <input
                type="text"
                required
                value={form.name}
                onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <input
                type="password"
                required
                value={form.password}
                onChange={(e) => setForm(f => ({ ...f, password: e.target.value }))}
                placeholder="Min 8 chars, upper, lower, digit, special char"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
              <select
                value={form.role}
                onChange={(e) => setForm(f => ({ ...f, role: e.target.value as 'admin' | 'sales' }))}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="sales">Sales</option>
                <option value="admin">Admin</option>
              </select>
            </div>

            {formError && <p className="sm:col-span-2 text-red-500 text-sm">{formError}</p>}
            {formSuccess && <p className="sm:col-span-2 text-green-600 text-sm">{formSuccess}</p>}

            <div className="sm:col-span-2">
              <button
                type="submit"
                disabled={creating}
                className="px-4 py-2 text-sm bg-gray-900 text-white rounded-lg hover:bg-gray-800 disabled:opacity-50"
              >
                {creating ? 'Creating...' : 'Create account'}
              </button>
            </div>
          </form>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Staff accounts ({loading ? '…' : staff.length})</h2>
          </div>
          {loading ? (
            <div className="flex items-center justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" /></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                    <th className="px-6 py-3">Name</th>
                    <th className="px-6 py-3">Email</th>
                    <th className="px-6 py-3">Role</th>
                    <th className="px-6 py-3">Status</th>
                    <th className="px-6 py-3">Last Login</th>
                    <th className="px-6 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {staff.map((member) => (
                    <tr key={member._id} className="hover:bg-gray-50">
                      <td className="px-6 py-3 font-medium text-gray-900">{member.name}</td>
                      <td className="px-6 py-3 text-gray-600">{member.email}</td>
                      <td className="px-6 py-3">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${ROLE_BADGE_STYLES[member.role]}`}>
                          {ROLE_LABELS[member.role]}
                        </span>
                      </td>
                      <td className="px-6 py-3">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${member.isActive ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                          {member.isActive ? 'Active' : 'Deactivated'}
                        </span>
                      </td>
                      <td className="px-6 py-3 text-gray-400">
                        {member.lastLogin ? new Date(member.lastLogin).toLocaleDateString() : 'Never'}
                      </td>
                      <td className="px-6 py-3 text-right">
                        {member.role !== 'superadmin' && (
                          <button
                            onClick={() => toggleStatus(member)}
                            className={member.isActive ? 'text-red-600 hover:text-red-800 font-medium' : 'text-blue-600 hover:text-blue-800 font-medium'}
                          >
                            {member.isActive ? 'Deactivate' : 'Activate'}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default StaffManagement;
